// DS: MO 3.7
import React from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import type { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink/PlainButtonOrLink';
import IdentityVertical from '@wix/da-shared-react/pkg/User/IdentityVertical';
import { getInfoToDisplayImageDeviation } from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/Image/_helpers';
import s from './SellerCard.scss';

interface Props {
  user: PapiUser;
  width: number;
  withShopCta?: boolean;
}

const getBackgroundUrl = (deviation): string | undefined => {
  const info = getInfoToDisplayImageDeviation({
    deviation,
    width: parseInt(s['background-picture-width']),
  });
  if (info) {
    return `url(${info.pickedImage.src})`;
  }
};

const BackgroundDeviation: React.FC<{
  deviation?: PapiDeviation;
  size: 'large' | 'medium';
}> = ({ deviation, size }) => {
  return (
    <div
      role="presentation"
      className={c(
        s['deviation'],
        !deviation && s['no-image'],
        deviation && deviation.isMature && s['mature-blur'],
        s[`deviation-${size}`]
      )}
      style={{
        backgroundImage: deviation ? getBackgroundUrl(deviation) : undefined,
      }}
    />
  );
};

const SellerCard: React.FC<Props> = ({ user, width, withShopCta }) => {
  const { t } = useTranslation();

  return (
    <Section className={s['root']} style={{ width }}>
      <BackgroundDeviation
        deviation={user.profile?.profilePic}
        size={withShopCta ? 'medium' : 'large'}
      />
      <PlainButtonOrLink
        role="presentation"
        aria-label={t(
          'shopPage.sellYourArt.strips.featuredShops.sellerCard.a11y.linkOverlay.label'
        )}
        href={Url.userShopLink(user.username, USER_PROFILE_SHOP_SUB_PATHS.ALL)}
        className={s['link-underlay']}
      />
      <div className={s['content']}>
        <IdentityVertical
          size="large"
          user={user}
          className={s['pointer-events']}
        />
        {withShopCta && (
          <SolidButton
            size="small"
            variant="main"
            href={Url.userShopLink(
              user.username,
              USER_PROFILE_SHOP_SUB_PATHS.ALL
            )}
            className={s['pointer-events']}
          >
            {t(
              'shopPage.sellYourArt.strips.featuredShops.sellerCard.button.label'
            )}
          </SolidButton>
        )}
      </div>
    </Section>
  );
};

SellerCard.displayName = 'SellerCard';

export default SellerCard;
